import { FilterSelection } from '@components/common/SearchWithFilter/SearchWithFilter.component'
import { ICampaignRequest } from '@dataTypes/marketing.type'
import {
  deleteCampaign,
  fetchAllCampaigns,
  updateCampaign,
} from '@store/actions/marketing.action'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { SelectEventArgs } from '@syncfusion/ej2-react-dropdowns'
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react'

interface MarketingTemplateHooks {
  activeTab: 'list' | 'calendar'
  filteredCampaigns: ICampaignRequest[]
  handleAdvancedSend: (campaign: ICampaignRequest) => void
  handleDropdownAction: (e: SelectEventArgs) => void
  handleFilterChange: (selectedFilters: FilterSelection) => void
  handleReactivateCampaign: (campaign: ICampaignRequest) => void
  handleSearch: (args: ChangeEvent<HTMLInputElement>) => void
  handleToggleStatus: (checked: boolean, rowData: ICampaignRequest) => void
  handleViewCampaign: (campaign: ICampaignRequest) => void
  hasNoCampaigns: boolean
  isReactivateCampaign: boolean
  onConfirmDelete: () => void
  onHideModal: () => void
  searchQuery: string
  selectedCampaign: ICampaignRequest | null
  setActiveTab: Dispatch<SetStateAction<'list' | 'calendar'>>
  setSelectedDropdownCampaignId: Dispatch<SetStateAction<string>>
  showAdvancedSendModal: boolean
  showDeleteModal: boolean
  showEditCampaignModal: boolean
  showPreviewCampaign: boolean
  updateSelectedCampaign: (campaign: ICampaignRequest) => void
}

const useMarketingCampaign = (): MarketingTemplateHooks => {
  const dispatch = useAppDispatch()
  const campaigns = useAppSelector((state) => state.marketing.campaigns)
  const clinic = useAppSelector((state) => state.clinic.selectedClinic)

  const [showAdvancedSendModal, setShowAdvancedSendModal] =
    useState<boolean>(false)
  const [showPreviewCampaign, setShowPreviewCampaign] = useState<boolean>(false)
  const [showEditCampaignModal, setShowEditCampaignModal] =
    useState<boolean>(false)
  const [selectedDropdownCampaignId, setSelectedDropdownCampaignId] =
    useState<string>('')
  const [selectedCampaign, setSelectedCampaign] =
    useState<ICampaignRequest | null>(null)
  const [activeTab, setActiveTab] = useState<'list' | 'calendar'>('list')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [filters, setFilters] = useState<FilterSelection>({
    messageStatus: [],
    campaignStatus: [],
    sentVia: [],
    campaignType: [],
  })
  const [filteredCampaigns, setFilteredCampaigns] =
    useState<ICampaignRequest[]>(campaigns)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [isReactivateCampaign, setIsReactivateCampaign] =
    useState<boolean>(false)

  const handleViewCampaign = (campaign: ICampaignRequest): void => {
    setSelectedCampaign(campaign)
    setShowPreviewCampaign(true)
  }

  const handleAdvancedSend = (campaign: ICampaignRequest): void => {
    setSelectedCampaign(campaign)
    setShowAdvancedSendModal(true)
  }

  const handleReactivateCampaign = (campaign: ICampaignRequest): void => {
    setSelectedCampaign(campaign)
    setIsReactivateCampaign(true)
    setShowEditCampaignModal(true)
  }

  const updateSelectedCampaign = (campaign: ICampaignRequest): void => {
    setSelectedCampaign(campaign)
  }

  const handleSearch = (args: ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(args.target.value)
  }

  const handleFilterChange = (selectedFilters: FilterSelection): void => {
    setFilters({ ...filters, ...selectedFilters })
  }

  const handleDropdownAction = (e: SelectEventArgs): void => {
    const action = e.item.id
    if (action === 'edit') {
      setShowEditCampaignModal(true)
    } else if (action === 'delete') {
      setShowDeleteModal(true)
    }
  }

  const onHideModal = (): void => {
    setIsReactivateCampaign(false)
    setShowAdvancedSendModal(false)
    setShowDeleteModal(false)
    setShowEditCampaignModal(false)
    setShowPreviewCampaign(false)
    setSelectedCampaign(null)
  }

  const onConfirmDelete = (): void => {
    if (selectedCampaign && selectedCampaign.campaign_id) {
      dispatch(deleteCampaign(selectedCampaign.campaign_id))
    }
    setSelectedCampaign(null)
  }

  const handleToggleStatus = (
    checked: boolean,
    rowData: ICampaignRequest,
  ): void => {
    const updatedData: Partial<ICampaignRequest> = {
      campaign_status: checked ? 'Active' : 'Inactive',
    }
    if (rowData.campaign_id) {
      dispatch(
        updateCampaign({ campaign_id: rowData.campaign_id, data: updatedData }),
      )
    }
  }

  useEffect(() => {
    if (campaigns.length > 0) {
      let _campaigns = [...campaigns]
      if (selectedDropdownCampaignId.length > 0) {
        _campaigns = _campaigns.filter(
          (campaign) => campaign.campaign_id === selectedDropdownCampaignId,
        )
      }
      _campaigns = _campaigns.filter((campaign) => {
        if (searchQuery.length !== 0) {
          return (
            campaign.name
              .toLowerCase()
              .includes(searchQuery.trim().toLowerCase()) ||
            campaign.description
              ?.toLowerCase()
              .includes(searchQuery.trim().toLowerCase())
          )
        } else return true
      })

      _campaigns = _campaigns.filter((campaign) => {
        if (filters.messageStatus.length !== 0) {
          return filters.messageStatus.includes(campaign.message_status)
        } else return true
      })

      _campaigns = _campaigns.filter((campaign) => {
        if (filters.campaignStatus.length !== 0) {
          return filters.campaignStatus.includes(campaign.campaign_status)
        } else return true
      })

      _campaigns = _campaigns.filter((campaign) => {
        if (filters.sentVia.length !== 0) {
          return filters.sentVia.includes(campaign.channel)
        } else return true
      })

      _campaigns = _campaigns.filter((campaign) => {
        if (filters.campaignType.length !== 0) {
          return campaign.type && filters.campaignType.includes(campaign.type)
        } else return true
      })
      setFilteredCampaigns(_campaigns)
    } else setFilteredCampaigns([])
  }, [searchQuery, filters, campaigns, selectedDropdownCampaignId])

  useEffect(() => {
    if (clinic) {
      dispatch(fetchAllCampaigns())
    }
  }, [dispatch, clinic])

  return {
    activeTab,
    filteredCampaigns,
    handleAdvancedSend,
    handleDropdownAction,
    handleFilterChange,
    handleReactivateCampaign,
    handleSearch,
    handleToggleStatus,
    handleViewCampaign,
    hasNoCampaigns: campaigns.length === 0,
    isReactivateCampaign,
    onConfirmDelete,
    onHideModal,
    searchQuery,
    selectedCampaign,
    setActiveTab,
    setSelectedDropdownCampaignId,
    showAdvancedSendModal,
    showDeleteModal,
    showEditCampaignModal,
    showPreviewCampaign,
    updateSelectedCampaign,
  }
}

export default useMarketingCampaign
